import { useCallback, useEffect, useState, useRef } from 'react';
import { useVoiceStore } from '@stores/voiceStore';
import { getCurrentLanguage } from '@/i18n';

/**
 * Available voices for different languages
 */
export interface VoiceOption {
  id: string;
  name: string;
  lang: string;
  localService: boolean;
}

/**
 * Map locale codes to speech synthesis language codes
 */
function mapLocaleToSpeechLang(locale: string): string {
  const map: Record<string, string> = {
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    de: 'de-DE',
  };
  return map[locale] || 'en-US';
}

/**
 * Get available voices for the current browser
 */
export function getAvailableVoices(): VoiceOption[] {
  if (!('speechSynthesis' in window)) {
    return [];
  }

  const voices = speechSynthesis.getVoices();
  return voices.map((voice) => ({
    id: voice.name,
    name: voice.name,
    lang: voice.lang,
    localService: voice.localService,
  }));
}

/**
 * Get the best voice for a given locale
 * Prefers local voices over remote for better offline support
 */
export function getBestVoiceForLocale(locale: string): SpeechSynthesisVoice | null {
  if (!('speechSynthesis' in window)) {
    return null;
  }

  const targetLang = mapLocaleToSpeechLang(locale);
  const voices = speechSynthesis.getVoices();

  if (voices.length === 0) {
    return null;
  }

  // Prefer local voices (better quality, offline support)
  const localVoices = voices.filter(
    (v) => v.localService && v.lang.startsWith(targetLang.slice(0, 2))
  );
  if (localVoices.length > 0) {
    return localVoices[0] ?? null;
  }

  // Fallback to any voice matching language
  const matchingVoice = voices.find((v) => v.lang.startsWith(targetLang.slice(0, 2)));
  if (matchingVoice) {
    return matchingVoice;
  }

  // Last resort: return first available voice
  return voices[0] ?? null;
}

/**
 * Get voice by name
 */
export function getVoiceByName(name: string): SpeechSynthesisVoice | null {
  if (!('speechSynthesis' in window)) {
    return null;
  }

  const voices = speechSynthesis.getVoices();
  return voices.find((v) => v.name === name) || null;
}

/**
 * Speak text using Web Speech API
 */
async function speakWithWebSpeech(
  text: string,
  voice: string,
  rate: number,
  locale: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!('speechSynthesis' in window)) {
      reject(new Error('Speech synthesis not supported'));
      return;
    }

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    // Small delay to allow cancel to complete (browser needs time to clean up)
    setTimeout(() => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = rate;
      utterance.lang = mapLocaleToSpeechLang(locale);
      utterance.volume = 1.0; // Ensure volume is at maximum

      console.log('🔊 TTS Speaking:', {
        text: text.substring(0, 50) + '...',
        rate,
        lang: utterance.lang,
        voice: voice,
        volume: utterance.volume
      });

      // Try to set preferred voice
      if (voice && voice !== 'default') {
        const preferredVoice = getVoiceByName(voice);
        if (preferredVoice) {
          utterance.voice = preferredVoice;
          console.log('✅ Using preferred voice:', preferredVoice.name);
        } else {
          console.log('⚠️ Preferred voice not found:', voice);
        }
      } else {
        // Use best voice for locale
        const bestVoice = getBestVoiceForLocale(locale);
        if (bestVoice) {
          utterance.voice = bestVoice;
          console.log('✅ Using best voice for locale:', bestVoice.name);
        } else {
          console.log('⚠️ No voice found for locale:', locale);
        }
      }

      utterance.onstart = () => {
        console.log('🎤 TTS started');
      };

      utterance.onend = () => {
        console.log('✅ TTS finished');
        resolve();
      };

      utterance.onerror = (event: SpeechSynthesisErrorEvent) => {
        // Ignore "canceled" errors as they're expected when we stop speech
        if (event.error === 'canceled') {
          console.log('⏹️ TTS canceled');
          resolve();
        } else {
          console.error('❌ TTS error:', event.error, event);
          reject(new Error(`Speech synthesis error: ${event.error}`));
        }
      };

      console.log('🚀 Calling speechSynthesis.speak()');
      speechSynthesis.speak(utterance);

      // Check if it's actually speaking
      setTimeout(() => {
        console.log('📊 speechSynthesis.speaking:', speechSynthesis.speaking);
        console.log('📊 speechSynthesis.pending:', speechSynthesis.pending);
        console.log('📊 speechSynthesis.paused:', speechSynthesis.paused);
      }, 200);
    }, 100);
  });
}

/**
 * React hook for text-to-speech functionality
 */
export function useTextToSpeech() {
  const {
    preferences,
    capabilities,
    isSpeaking,
    setIsSpeaking,
    currentUtterance,
    setCurrentUtterance,
  } = useVoiceStore();

  const [error, setError] = useState<string | null>(null);
  const [availableVoices, setAvailableVoices] = useState<VoiceOption[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load available voices
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      return;
    }

    // Voices may not be immediately available
    const loadVoices = () => {
      const voices = getAvailableVoices();
      setAvailableVoices(voices);
    };

    // Initial load
    loadVoices();

    // Listen for voices changed event (some browsers load voices asynchronously)
    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (speechSynthesis.onvoiceschanged !== undefined) {
        speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  /**
   * Speak the given text
   */
  const speak = useCallback(
    async (text: string) => {
      console.log('🎯 speak() called');

      setError(null);
      setIsSpeaking(true);
      setCurrentUtterance(text);
      abortControllerRef.current = new AbortController();

      try {
        const locale = getCurrentLanguage();
        await speakWithWebSpeech(text, preferences.ttsVoice, preferences.ttsSpeed, locale);
      } catch (err) {
        // Only log real errors, not cancellations
        if (err instanceof Error && !err.message.includes('canceled')) {
          console.error('TTS error:', err);
          setError(err.message);
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setIsSpeaking(false);
          setCurrentUtterance(null);
        }
      }
    },
    [preferences.ttsVoice, preferences.ttsSpeed]
  );

  /**
   * Stop speaking
   */
  const stop = useCallback(() => {
    abortControllerRef.current?.abort();

    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }

    setIsSpeaking(false);
    setCurrentUtterance(null);
    setError(null);
  }, [setIsSpeaking, setCurrentUtterance]);

  /**
   * Pause speaking (if supported)
   */
  const pause = useCallback(() => {
    if ('speechSynthesis' in window && speechSynthesis.speaking) {
      speechSynthesis.pause();
    }
  }, []);

  /**
   * Resume speaking (if supported)
   */
  const resume = useCallback(() => {
    if ('speechSynthesis' in window && speechSynthesis.paused) {
      speechSynthesis.resume();
    }
  }, []);

  return {
    speak,
    stop,
    pause,
    resume,
    isSpeaking,
    currentUtterance,
    error,
    availableVoices,
    isSupported: capabilities.webSpeech,
  };
}

/**
 * Initialize voice capabilities detection
 * Should be called once on app startup
 */
export async function detectVoiceCapabilities(): Promise<void> {
  const { updateCapabilities } = useVoiceStore.getState();

  const capabilities = {
    webSpeech: 'speechSynthesis' in window && 'SpeechRecognition' in window,
    webgpu: false,
    whisperLoaded: false,
    kokoroLoaded: false,
  };

  // Check WebGPU availability (for future Phase 2)
  if ('gpu' in navigator) {
    try {
      const gpu = (navigator as any).gpu;
      const adapter = await gpu?.requestAdapter();
      capabilities.webgpu = !!adapter;
    } catch {
      capabilities.webgpu = false;
    }
  }

  updateCapabilities(capabilities);
}
